import { AbsoluteCenter, Center, useToast } from '@chakra-ui/react';
import { motion } from "framer-motion";
import { t } from 'i18next';
import { isEmpty } from "lodash";
import { useEffect, useRef, useState } from "react";
import styled from "styled-components";
import ToastMessageBox from '../../components/alerts/ToastMessageBox';
import SwipedBuddyPreference from '../../components/api/mutations/SwipedBuddyPreference';
import ToggleFollow from "../../components/api/mutations/ToggleFollow";
import useBuddyPreferenceQuery from "../../components/api/queries/useBuddyPreferenceQuery";
import useBuddyUserProfilesQuery from "../../components/api/queries/useBuddyUserProfilesQuery";
import RegularSolidButton from "../../components/buttons/RegularSolidButton";
import MutualMembersBottomDrawer from "../../components/drawer/MutualMembersBottomDrawer";
import { FanBuddyCardDialog } from "../../components/fan-buddy/FanBuddyCardDialog";
import FanBuddySwipeCard from "../../components/fan-buddy/FanBuddySwipeCard";
import { FollowedDialog } from "../../components/fan-buddy/FollowedDialog";
import StickyHeader from "../../components/StickyHeader";
import ProfileAvatar from "../../components/thumbnails/ProfileAvatar";
import LightColors from "../../constants/LightColors";
import { ReactComponent as ArrowReplayIcon } from "../../icons/arrow.reply.svg";
import { ReactComponent as ArrowTailIcon } from "../../icons/arrow.tail.svg";
import { openNewTab } from '../../utils/UIUtils';

const ActionButtonGroup = styled.div`
  display: flex;
  gap: 30px;
  padding: 12px 16px;
  align-items: center;
`

const InvitationTitle = styled.div`
  font-weight: 600;
  font-size: 20px;
  line-height: 24px;
  text-align: center;
  margin-bottom: 14px;
  color: ${LightColors.textmainlv1};
`

const InvitationDescription = styled.div`
  font-weight: 500;
  font-size: 15px;
  line-height: 18px;
  text-align: center;
  color: ${LightColors.textmainlv4};
	white-space: pre-wrap;
  margin-bottom: 24px;
`

export default function InviteFanBuddyPage() {
  const toast = useToast();
  const swipeCardRef = useRef();

  const [isShowMutualMemberBottomSheet, setIsShowMutualMemberBottomSheet] = useState(false);
  const [selectedBuddyUserProfile, setSelectedBuddyUserProfile] = useState();
  const [currentIndex, setCurrentIndex] = useState(0);
  const [isAlertOpen, setIsAlertOpen] = useState(false)
  const [isShowFollowedDialog, setIsShowFollowedDialog] = useState(false);
  const [remainingSwipeCount, setRemainingSwipeCount] = useState(0);

  const currentUserBuddyPreference = useBuddyPreferenceQuery();

  useEffect(() => {
    if(currentUserBuddyPreference) {
      setRemainingSwipeCount(currentUserBuddyPreference.remainingSwipeCount)
    }
  }, [currentUserBuddyPreference])


  const { data: profiles, fetchNextPage, hasNextPage } = useBuddyUserProfilesQuery();
  const buddyPreferences = profiles?.pages?.flatMap((page) =>
    page.buddyPreferences.map((buddyPreference) => buddyPreference)
  )

  useEffect(() => {
    if(currentIndex % 7 === 0 && hasNextPage) {
      fetchNextPage();
    }
  }, [currentIndex, hasNextPage, fetchNextPage])

  const buddyUserProfile = buddyPreferences?.[currentIndex]

  const hasPrevProfile = () => {
    return currentIndex !== 0
  }

  const hasNextProfile = () => {
    return (hasNextPage || (currentIndex + 1) < buddyPreferences?.length)
  }

  const handlePrev = async () => {
    if(!hasPrevProfile()) return;

    const newIndex = Math.max(currentIndex - 1, 0)
    if (newIndex !== currentIndex) {
      if (swipeCardRef.current) {
        await swipeCardRef.current.triggerSwipeAnimation('left')
      }
      setCurrentIndex(newIndex)
    }
  }

  const handleNext = async () => {
    if (swipeCardRef.current && hasNextProfile() && remainingSwipeCount > 0) {
      const newIndex = Math.min(currentIndex + 1, buddyPreferences?.length - 1 || 0)
      if (newIndex !== currentIndex) {
        SwipedBuddyPreference(buddyUserProfile.userProfileId)
        await swipeCardRef.current.triggerSwipeAnimation('right')
        setCurrentIndex(newIndex)
        setRemainingSwipeCount(remainingSwipeCount - 1)
      }
    }
  }

  const handleFollow = async () => {
    if (isEmpty(currentUserBuddyPreference) || isEmpty(buddyUserProfile)) {
      return;
    }
    const response = await ToggleFollow({
      isFollowed: false,
      currentUserProfileId: currentUserBuddyPreference.userProfileId,
      followUserProfileId: buddyUserProfile.userProfileId
    });

    if (response?.ok) {
      setIsShowFollowedDialog(true);
    }
  }

  const onClosedFollowedDialog = () => {
    handleNext()
  }

  const handleSelectedMutualMembers = (profile) => {
    setSelectedBuddyUserProfile(profile)
    setIsShowMutualMemberBottomSheet(true)
  }

  const navigateBuddyMatching = (buddyPreference) => {
    openNewTab(`/fan-buddy/matching?sourceUserProfileId=${currentUserBuddyPreference.userProfileId}&targetUserProfileId=${buddyPreference.userProfileId}`)
  }

  const navigateBuddyUserProfile = (buddyPreference) => {
    openNewTab(`/feed/user_profile/${buddyPreference.userProfileId}`)
  }

  const showAlertFanBuddyCard = () => {
    setIsAlertOpen(true)
  }

  const copyShareUrl = () => {
    try {
      navigator.clipboard.writeText("https://app.pocapan.com/fan-buddy/intro")
      toast({
        duration: 1500,
        render: () => (
          <ToastMessageBox message={t("chatRoom.copyMessageToClipboard")}/>
        ),
      })
    } catch (error) {
      console.log(error)
    }
  }

  return (
    <div
      style={{
        height: '100vh',
        background:
          'linear-gradient(180deg, #FFF 0%, #FFF 5.61%, rgba(255, 255, 255, 0.30) 19.79%, #FFF 32.46%, rgba(255, 255, 255, 0.50) 68.5%, #FFF 100%), conic-gradient(from 5deg at 55.73% 56.22%, #A6F1FF 0deg, #CFFFBD 108.00000429153442deg, #FFBAD2 216.00000858306885deg, #FFF3B2 323.99999141693115deg, #A6F1FF 360deg)'
      }}
    >
      <StickyHeader
        showBackButton={true}
        showTitle={true}
        alwaysShowTitle={true}
        title={t('InviteFanBuddyPage.pageTitle')}
        transparent={true}
        rightContent={
          <ProfileAvatar
            profileImageUrl={currentUserBuddyPreference?.thumbnailUrl}
            widthAndHeight={'29px'}
            svgWidthAndHeight={'18px'}
            onClick={() => showAlertFanBuddyCard()}
          />
        }
      />
      <AbsoluteCenter
        style={{
          marginTop: 'calc(12px + env(safe-area-inset-top))',
          padding: '0 24px',
          width: '100%',
          maxWidth: 'calc(var(--chakra-sizes-xl))'
        }}
      >
        {!isEmpty(buddyPreferences) &&
          (hasNextProfile() ? (
            <div className="flex flex-col items-center pl-4 pr-4 relative">
              <FanBuddySwipeCard
                ref={swipeCardRef}
                currentUserKeywords={currentUserBuddyPreference.keywords}
                buddyUserProfile={buddyUserProfile}
                onSelectedMatching={navigateBuddyMatching}
                onSelectedMutualMembers={handleSelectedMutualMembers}
                onSelectedBuddyUserProfile={navigateBuddyUserProfile}
              />
            </div>
          ) : (
            <Center className="flex flex-col text-center">
              <InvitationTitle>{t('InviteFanBuddyPage.notAvailableFanBuddy')}</InvitationTitle>
              <InvitationDescription>
                {t('InviteFanBuddyPage.notAvailableFanBuddyDescription')}
              </InvitationDescription>
              <RegularSolidButton
                text={t('InviteFanBuddyPage.inviteButton')}
                paddingX={'18px'}
                paddingY={'11px'}
                fontWeight={600}
                fontSize={'14px'}
                lineHeight={'20px'}
                onClick={() => copyShareUrl()}
              />
            </Center>
          ))}
      </AbsoluteCenter>
      {/* Action Buttons */}
      {!isEmpty(buddyPreferences) && (
        <motion.div
          initial={{ opacity: 0.5 }}
          animate={{ opacity: 1 }}
          transition={{ duration: 0.3 }}
        >
          <ActionButtonGroup
            className="bottomNavbar"
            style={{ marginBottom: 'calc(env(safe-area-inset-top))', background: 'transparent' }}
          >
            <button
              type="button"
              onClick={handlePrev}
              className="w-12 h-12 rounded-full flex items-center justify-center bg-white"
              style={{
                boxShadow: '0px 4px 4px 0px rgba(0, 0, 0, 0.1), 0px 2px 1px 0px rgba(0, 0, 0, 0.05)'
              }}
            >
              <ArrowReplayIcon
                width={24}
                height={24}
                fill={hasPrevProfile() ? LightColors.textmainlv2 : LightColors.disable}
              />
            </button>
            {
              <RegularSolidButton
                disabled={!hasNextProfile()}
                text={t('InviteFanBuddyPage.followButton')}
                onClicked={handleFollow}
                style={{
                  flexGrow: 1,
                  background: LightColors.primary,
                  height: '52px',
                  color: LightColors.onPrimary,
                  fontSize: '17px'
                }}
              />
            }

            <div className="relative">
              <button
                type="button"
                onClick={handleNext}
                className="w-12 h-12 rounded-full flex items-center justify-center bg-white"
                style={{
                  boxShadow:
                    '0px 4px 4px 0px rgba(0, 0, 0, 0.1), 0px 2px 1px 0px rgba(0, 0, 0, 0.05)'
                }}
              >
                <ArrowTailIcon
                  width={24}
                  height={24}
                  fill={hasNextProfile() ? LightColors.primary : LightColors.disable}
                />
              </button>
              {hasNextProfile() && (
                <div
                  className="absolute rounded-full flex items-center justify-center"
                  style={{
                    top: -2,
                    right: -2,
                    width: '18px',
                    height: '18px',
                    background: 'linear-gradient(137.49deg, #FF5ACD 3.99%, #FF4C4C 100%)',
                    boxShadow: '0px 1px 2px 0px rgba(0, 0, 0, 0.05)'
                  }}
                >
                  <span className="text-white" style={{ fontSize: '13px' }}>
                    {remainingSwipeCount}
                  </span>
                </div>
              )}
            </div>
          </ActionButtonGroup>
        </motion.div>
      )}
      {isShowMutualMemberBottomSheet && (
        <MutualMembersBottomDrawer
          isOpen={isShowMutualMemberBottomSheet}
          onClose={() => setIsShowMutualMemberBottomSheet(false)}
          members={selectedBuddyUserProfile.mutualMembers}
        />
      )}
      {isAlertOpen && (
        <FanBuddyCardDialog
          isAlertOpen={isAlertOpen}
          isMine={true}
          setIsAlertOpen={setIsAlertOpen}
          buddyPreference={currentUserBuddyPreference}
        />
      )}
      {isShowFollowedDialog && (
        <FollowedDialog
          isAlertOpen={isShowFollowedDialog}
          setIsAlertOpen={setIsShowFollowedDialog}
          isFollowed={buddyUserProfile?.isFollowing}
          onClosed={onClosedFollowedDialog}
        />
      )}
    </div>
  );
}
